import React, { useRef, useEffect } from 'react';
import LineElement from './LineElement';
import { createPreventionProps } from '@web/utils/copy-paste-prevention.utils';
import { extractClassName, extractLineContent } from '@web/utils/text-processing.utils';
import { parseLineToWords } from '@web/utils/line-parsing.utils';
import { ITextEntity, TextDisplayMode } from '@shared/types';
import { useStoreByName } from '@web/hooks/use-stores.hooks';
import { observer } from 'mobx-react-lite';
import { Info } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

// ---- טיפוסים ----
interface TextElementProps {
  mode: TextDisplayMode;
  fontClassName: string;
  wordStatuses?: any[]; // עבור תצוגת תוצאות סטטיות (במצב result בלבד)
  preventCopyPaste?: boolean; // האם למנוע העתקה/הדבקה
  containerRef?: React.RefObject<HTMLDivElement>; // ref לcontainer
  showArrowIndicator?: boolean; // האם להציג חץ אינדיקטור
  /**
   * The text to render. Defaults to the book the reader picked, which is where
   * every existing caller gets it. The Siddur reader passes its own, because
   * its text is a node in a tree and was never in booksStore.
   */
  textEntity?: ITextEntity | null;
}

/**
 * רכיב מעטפת המעבד מערך של שורות HTML ויוצר LineElement לכל שורה.
 * מנהל ref משותף לספירת מילים ומטפל במניעת copy/paste.
 * 
 * שני מצבי פעולה:
 * 1. מצבי קריאה (reading/byHeart) - הסטטוסים מגיעים ישירות מה-speechRecognitionStore
 * 2. מצב תוצאות (result) - הסטטוסים מועברים כ-wordStatuses prop לתצוגה סטטית
 * 
 * הרכיב ניגש ישירות ל-htmlTextLines מ-booksStore.
 */
const TextElement = observer(
  ({
    mode,
    fontClassName,
    wordStatuses,
    preventCopyPaste = false,
    containerRef,
    showArrowIndicator = true,
    textEntity,
  }: TextElementProps) => {
    // Hook לתרגום
    const { t } = useTranslation();
    
    // גישה ישירה לסטור לקבלת הטקסט
    const booksStore = useStoreByName('booksStore') as any;
    const selectedTextEntity = textEntity ?? booksStore.selectedTextEntity;
    const readingSessionStore = useStoreByName('readingSessionStore') as any;
    
    // בדיקה ש-selectedTextEntity קיים לפני destructuring
    if (!selectedTextEntity) {
      return (
        <div className="card no-text-message translatable-content">
          <Info className="icon" />
          <h3>{t("textNotAvailable")}</h3>
          <p>{t("textWillBeAddedSoon")}</p>
        </div>
      );
    }
    
    const { htmlTextLines } = selectedTextEntity;
    
    // קבלת המילים מה-ReadingSession אם קיים (במצבי reading/byHeart)
    const readingWords = mode === 'reading' || mode === 'byHeart' 
      ? readingSessionStore.allWords 
      : undefined;

    // יצירת props למניעת copy/paste
    const preventionProps = createPreventionProps(preventCopyPaste);

    // חישוב האינדקס ההתחלתי לכל שורה
    let wordIndexCounter = 0;

    
    // אם אין טקסט זמין, הצג כרטיס התנצלות
    if (!htmlTextLines || htmlTextLines.length === 0) {
      return (
        <div className="card no-text-message translatable-content">
          <Info className="icon" />
          <h3>{t("textNotAvailable")}</h3>
          <p>{t("textWillBeAddedSoon")}</p>
        </div>
      );
    }
    
    return (
      <div ref={containerRef} className={`text-element text-wrapper ${fontClassName}`} lang="he" {...preventionProps}>
        {htmlTextLines.map((line: string, index: number) => {
          const startWordIndex = wordIndexCounter;
          const lineContent = extractLineContent(line);
          const parsedWords = parseLineToWords(lineContent);
          wordIndexCounter += parsedWords.length;

          return (
            <LineElement
              key={index}
              content={lineContent}
              parsedWords={parsedWords}
              startWordIndex={startWordIndex}
              mode={mode}
              fontClassName={fontClassName}
              wordStatuses={wordStatuses}
              showArrowIndicator={showArrowIndicator}
              className={extractClassName(line)}
              readingWords={readingWords}
            />
          );
        })}
      </div>
    );
  }
);

TextElement.displayName = 'TextElement';

export default TextElement;
